import React, { useEffect, useMemo, useState } from "react";
import TableGeneric from "../../../../shared/components/generic/table/table-generic";
import Pagination from "../../../../shared/components/generic/pagination/pagination";
import { showConfirmAlert } from "../../../../shared/components/generic/alert/confirm-alert/confirm-alert";
import { showCommonAlert } from "../../../../shared/components/generic/alert/utils/show-common-alert-utils";
import FormModal from "../../../../shared/components/generic/form/form-modal/form-modal";
import { segmentationColumnsConfig } from "../../config/segmentacao/segmentation-columns-config";
import { segmentationFormConfig } from "../../config/segmentacao/segmentation-form-config";
import AdminSection from "../../components/admin-section/admin-section";
import { useAdminFormModal } from "../../hooks/useAdminFormModal";
import {
	confirmSaveChanges,
	showNoRecordsFoundAlert
} from "../../utils/admin-utils";
import { useSegmentation } from "../../hooks/segmentacao/useSegmentation";
import { useSegmentationUpdate } from "../../hooks/segmentacao/useSegmentationUpdate";
import { useSegmentationDelete } from "../../hooks/segmentacao/useSegmentationDelete";
import { useSegmentationById } from "../../hooks/segmentacao/useSegmentationById";
import { useSegmentations } from "../../hooks/segmentacao/useSegmentations";
import {
	hasValidationErrors,
	removeClearedValidationErrors
} from "../../../utils/validation-utils";
import PageLoading from "../../../../shared/components/generic/page-loading/page-loading";

const isConfirmAccepted = result => {
	return result?.isConfirmed === true || result?.value === true;
};

const Segmentacao = () => {
	const segmentationModal = useAdminFormModal();

	const [segmentationActionLoadingMessage, setSegmentationActionLoadingMessage] = useState("");
	const [segmentationValidationErrors, setSegmentationValidationErrors] = useState({});
	const [clearedValidationErrors, setClearedValidationErrors] = useState({});
	const [segmentationPagination, setSegmentationPagination] = useState({
		totalElements: 0,
		totalPages: 0,
		page: 0,
		size: 10
	});

	const {
		segmentations,
		loading: segmentationsLoading,
		refetch: fetchSegmentations
	} = useSegmentations();

	const {
		loading: segmentationByIdLoading,
		fetchSegmentationById
	} = useSegmentationById();

	const {
		loading: createSegmentationLoading,
		validateSegmentation,
		createSegmentation
	} = useSegmentation();

	const {
		loading: updateSegmentationLoading,
		updateSegmentation
	} = useSegmentationUpdate();

	const {
		loading: deleteSegmentationLoading,
		deleteSegmentation
	} = useSegmentationDelete();

	const isLoadingSegmentation =
		segmentationsLoading ||
		segmentationByIdLoading;

	const isSavingSegmentation =
		createSegmentationLoading ||
		updateSegmentationLoading;

	const isDeletingSegmentation = deleteSegmentationLoading;

	const loadSegmentations = async (
		pagination = segmentationPagination,
		{ showEmptyAlert = true } = {}
	) => {
		const result = await fetchSegmentations({
			page: pagination.page,
			size: pagination.size
		});

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar as segmentações.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return result;
		}

		setSegmentationPagination(result.pagination);

		if (result.empty && showEmptyAlert) {
			await showNoRecordsFoundAlert();
		}

		return result;
	};

	useEffect(() => {
		loadSegmentations({
			page: 0,
			size: segmentationPagination.size
		});
	}, []);

	const visibleSegmentationValidationErrors = removeClearedValidationErrors({
		errors: segmentationValidationErrors,
		clearedErrors: clearedValidationErrors
	});

	const handlePageChange = ({ selected }) => {
		loadSegmentations({
			...segmentationPagination,
			page: selected
		});
	};

	const handleOpenCreate = () => {
		setSegmentationValidationErrors({});
		setClearedValidationErrors({});
		segmentationModal.openCreate();
	};

	const handleEdit = async row => {
		setSegmentationValidationErrors({});
		setClearedValidationErrors({});

		const result = await fetchSegmentationById(row.id);

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar os dados da segmentação.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		segmentationModal.openEdit(result.segmentation);
	};

	const handleCloseSegmentationModal = () => {
		setSegmentationValidationErrors({});
		setClearedValidationErrors({});
		segmentationModal.close();
	};

	const handleSegmentationFieldChange = ({ name }) => {
		setClearedValidationErrors(previous => ({
			...previous,
			[name]: true
		}));
	};

	const handleSubmitSegmentation = async values => {
		const selectedItem = segmentationModal.selectedItem;
		const isEditing = segmentationModal.isEditing;

		const validationErrors = validateSegmentation(values);

		if (hasValidationErrors(validationErrors)) {
			setClearedValidationErrors({});
			setSegmentationValidationErrors(validationErrors);

			await showCommonAlert({
				title: "Atenção",
				html: Object.values(validationErrors).join("<br />"),
				icon: "warning",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		setSegmentationValidationErrors({});
		setClearedValidationErrors({});
		segmentationModal.close();

		const confirmResult = await confirmSaveChanges({
			description: "Deseja salvar as alterações?"
		});

		if (!isConfirmAccepted(confirmResult)) {
			return;
		}

		let saveResult;

		setSegmentationActionLoadingMessage("Salvando segmentação...");

		try {
			saveResult = isEditing
				? await updateSegmentation(selectedItem.id, values)
				: await createSegmentation(values);

			if (saveResult.ok) {
				await loadSegmentations({
					page: isEditing ? segmentationPagination.page : 0,
					size: segmentationPagination.size
				});
			}
		} finally {
			setSegmentationActionLoadingMessage("");
		}

		if (!saveResult.ok) {
			await showCommonAlert({
				title: "Erro",
				message: saveResult.error,
				fallbackMessage: "Não foi possível salvar a segmentação.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		await showCommonAlert({
			title: "Sucesso",
			message: isEditing
				? "Segmentação atualizada com sucesso."
				: "Segmentação incluída com sucesso.",
			icon: "success",
			customClass: {
				container: "common-alert__container",
				confirmButton: "alert-form-button"
			}
		});
	};

	const handleDelete = row => {
		showConfirmAlert({
			title: "Exclusão",
			description: `Deseja excluir "${row.segmentation}"?`,
			confirmLabel: "Sim, excluir",
			cancelLabel: "Cancelar"
		}).then(async result => {
			if (!isConfirmAccepted(result)) {
				return;
			}

			let deleteResult;
			let reloadResult;

			setSegmentationActionLoadingMessage("Excluindo segmentação...");

			try {
				deleteResult = await deleteSegmentation(row.id);

				if (deleteResult.ok) {
					reloadResult = await loadSegmentations(
						{
							page: segmentationPagination.page,
							size: segmentationPagination.size
						},
						{
							showEmptyAlert: false
						}
					);
				}
			} finally {
				setSegmentationActionLoadingMessage("");
			}

			if (!deleteResult.ok) {
				await showCommonAlert({
					title: "Erro",
					message: deleteResult.error,
					fallbackMessage: "Não foi possível excluir a segmentação.",
					icon: "error",
					customClass: {
						container: "common-alert__container",
						confirmButton: "alert-form-button"
					}
				});

				return;
			}

			await showCommonAlert({
				title: "Sucesso",
				message: "Segmentação excluída com sucesso.",
				icon: "success",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			if (reloadResult?.empty) {
				await showNoRecordsFoundAlert();
			}
		});
	};

	const columns = useMemo(() => {
		return segmentationColumnsConfig({
			onEdit: handleEdit,
			onDelete: handleDelete
		});
	}, []);

	const formFields = useMemo(() => {
		return segmentationFormConfig({
			segmentation: segmentationModal.selectedItem
		});
	}, [segmentationModal.selectedItem]);

	return (
		<>
			<PageLoading
				disabled={segmentationModal.isOpen}
				actionMessage={segmentationActionLoadingMessage}
				states={[
					{
						loading: isLoadingSegmentation,
						message: "Carregando segmentações..."
					},
					{
						loading: isDeletingSegmentation,
						message: "Excluindo segmentação..."
					},
					{
						loading: isSavingSegmentation,
						message: "Salvando segmentação..."
					}
				]}
			/>
			<AdminSection
				title="Configuração de Tamanho de Cliente"
				actionLabel="Incluir tamanho"
				actionStartIcon="plusCircle"
				onAction={handleOpenCreate}
			>
				<TableGeneric
					columns={columns}
					data={segmentations}
					rowKey="id"
				/>

				{segmentationPagination.totalPages > 1 && (
					<Pagination
						pageCount={segmentationPagination.totalPages}
						currentPage={segmentationPagination.page}
						onPageChange={handlePageChange}
					/>
				)}
			</AdminSection>

			<FormModal
				open={segmentationModal.isOpen}
				title={segmentationModal.isEditing ? "Editar tamanho de cliente" : "Incluir tamanho de cliente"}
				formId="segmentation-form"
				formFields={formFields}
				errors={visibleSegmentationValidationErrors}
				onClose={handleCloseSegmentationModal}
				onSubmit={handleSubmitSegmentation}
				onFieldChange={handleSegmentationFieldChange}
				submitLabel="Salvar"
				cancelLabel="Cancelar"
				submitDisabled={
					isSavingSegmentation ||
					isDeletingSegmentation ||
					isLoadingSegmentation ||
					Boolean(segmentationActionLoadingMessage)
				}
			/>
		</>
	);
};

export default Segmentacao;